import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, resolveDirective as _resolveDirective, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7f99b6a3"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  class: "datepicker",
  "data-ts": "datepicker"
}
const _hoisted_2 = {
  key: 0,
  class: "datepicker__label"
}
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "bx bx-calendar" }, null, -1))
const _hoisted_4 = { class: "datepicker__display__dates" }
const _hoisted_5 = { class: "datepicker__display__dates__date" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "bx bx-minus text-neutral-400 text-weight-medium" }, null, -1))
const _hoisted_7 = { class: "datepicker__display__dates__date" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "bx bx-x text-md" }, null, -1))
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = {
  key: 1,
  class: "absolute text-error-700 text-sm"
}

import { ref, onMounted } from 'vue';
import { normalizeDate, stylizeDate } from './calendarUtils';
import CalendarMonth from './CalendarMonth.vue';

/**
 * DatePicker component displays from and until dates.
 * Set by the AppCalendar component.
 *
 * PROPS
 * @property {String} label -- Displays a title above the component.
 * @property {Date} predefinedStart -- Displays a preset date
 * @property {Date} predefinedEnd -- Displays a preset date
 * @property {Object} disabledDates -- Passed through to AppCalendar
 */


export default /*#__PURE__*/_defineComponent({
  __name: 'DatePicker',
  props: {
	label: {
		type: String,
	},
	placeholder: {
		type: Object,
		required: true,
		validator(value) {
			if (typeof value !== 'object') return false;
			if (!value.fromLabel || typeof value.fromLabel !== 'string') return false;
			if (value.toLabel && typeof value.toLabel !== 'string') return false;

			return true;
		},
	},
	predefinedStart: [Date, String],
	predefinedEnd: [Date, String],
	disabledDates: Object,
	hasError: {
		type: Boolean,
		default: false,
	},
	errorLabel: String,
	disabled: {
		type: Boolean,
		default: false,
	},
	isShowingControls: {
		type: Boolean,
		default: true,
	},
	isShowingRangeToggle: {
		type: Boolean,
		default: true,
	},
	hasRange: {
		type: Boolean,
		default: true,
	},
},
  emits: ["selectedPeriod", "selectedDate", "clearDates", "applySelection"],
  setup(__props, { expose: __expose, emit: __emit }) {

const emit = __emit;

const props = __props;

const showCalendar = ref(false);
const datesSelected = ref(false);
const startDay = ref();
const endDay = ref();
const hasRange = ref(props.hasRange);

const clearSelection = () => {
	startDay.value = null;
	endDay.value = null;
};

const hideCalendar = () => {
	showCalendar.value = false;
	if (!datesSelected.value && !props.predefinedStart) {
		clearSelection();
	}
};

const resetSelectedDates = () => {
	datesSelected.value = false;
	clearSelection();
	emit('clearDates');
};

const handleStartDaySelected = (date: Date) => {
	if (!hasRange.value) {
		emit('selectedDate', date);
	}
	startDay.value = stylizeDate(date);
};

const handleEndDaySelected = (date: Date) => {
	if (!hasRange.value) {
		clearSelection();
		// If singleDate is true, treat endDay selection as startDay selection
		handleStartDaySelected(date);
		return;
	}

	endDay.value = stylizeDate(date);

	emit('selectedPeriod', {
		from: normalizeDate(startDay.value),
		to: normalizeDate(endDay.value),
	});
};

const handleToggleHasRange = (hasRangeValue: boolean) => {
	hasRange.value = !hasRangeValue;
	if (!datesSelected.value) {
		clearSelection();
	} else {
		resetSelectedDates();
	}
};

const handleSelectedDates = () => {
	emit('applySelection', {
		from: normalizeDate(startDay.value),
		to: endDay.value ? normalizeDate(endDay.value) : null,
	});
	showCalendar.value = false;
	datesSelected.value = true;
};

onMounted(() => {
	if (props.predefinedStart) startDay.value = stylizeDate(props.predefinedStart);
	if (props.predefinedEnd) endDay.value = stylizeDate(props.predefinedEnd);
});

__expose({
	resetSelectedDates,
});

return (_ctx: any,_cache: any) => {
  const _directive_click_outside = _resolveDirective("click-outside")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(__props.label), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      "aria-label": "open calendar",
      title: "Show/Hide calendar",
      class: _normalizeClass([
				'datepicker__display',
				{ 'datepicker__display--error': __props.hasError },
				{ 'datepicker__display--disabled': __props.disabled },
			]),
      role: "button",
      tabindex: "0",
      "aria-haspopup": "true",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (showCalendar.value = !showCalendar.value)),
      onKeydown: _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (!__props.disabled && (showCalendar.value = !showCalendar.value)), ["prevent"]), ["enter"]))
    }, [
      _hoisted_3,
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("p", _hoisted_5, _toDisplayString(startDay.value || __props.placeholder.fromLabel), 1),
        (hasRange.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _hoisted_6,
              _createElementVNode("p", _hoisted_7, _toDisplayString(endDay.value || __props.placeholder.toLabel), 1)
            ], 64))
          : _createCommentVNode("", true)
      ])
    ], 34),
    _withDirectives(_createElementVNode("button", {
      disabled: __props.disabled,
      "aria-label": "Clear dates",
      name: "clearDatesBtn",
      class: "clear-button",
      "data-test": "clearDatesButton",
      type: "button",
      onClick: resetSelectedDates
    }, _hoisted_10, 8, _hoisted_8), [
      [_vShow, datesSelected.value || __props.predefinedStart]
    ]),
    _createVNode(_Transition, { name: "fade" }, {
      default: _withCtx(() => [
        (showCalendar.value)
          ? (_openBlock(), _createBlock(CalendarMonth, {
              key: 0,
              id: "calendarDropdown",
              "disabled-dates": __props.disabledDates,
              "predefined-start": startDay.value,
              "predefined-end": endDay.value,
              "has-range": hasRange.value,
              "is-showing-controls": __props.isShowingControls,
              "is-showing-range-toggle": __props.isShowingRangeToggle,
              class: "datepicker__calendar",
              onStartDaySelected: handleStartDaySelected,
              onEndDaySelected: handleEndDaySelected,
              onClearDates: clearSelection,
              onToggleHasRange: handleToggleHasRange,
              onApplyUserSelection: handleSelectedDates
            }, null, 8, ["disabled-dates", "predefined-start", "predefined-end", "has-range", "is-showing-controls", "is-showing-range-toggle"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (__props.hasError)
      ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(__props.errorLabel), 1))
      : _createCommentVNode("", true)
  ])), [
    [_directive_click_outside, hideCalendar]
  ])
}
}

})